import { ReactNode, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Button from '../Button';

let openOffCanvasCount = 0;

interface OffCanvasProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  position?: 'left' | 'right';
  title?: string;
  width?: string;
  mobileWidth?: string;
  additionalHeaderContent?: ReactNode;
  showFooter?: boolean;
  footerContent?: ReactNode;
  zIndex?: number;
  backdropOpacity?: number;
}

/**
 * Reusable OffCanvas component that can be used for various slideover panels
 * Responsive for mobile and desktop devices
 */
const OffCanvas = ({ 
  isOpen, 
  onClose, 
  children, 
  position = 'right', 
  title = 'Panel', 
  width = '50%',
  mobileWidth = '100%',
  additionalHeaderContent,
  showFooter = true,
  footerContent,
  zIndex = 1050,
  backdropOpacity = 0.5
}: OffCanvasProps) => {
  // Track window width for responsive behavior
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768; // Standard breakpoint for mobile

  // Update window width when it changes
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Add a backdrop click handler that prevents event propagation
  const handleBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the event from bubbling up
    onClose();
  };
  
  // Prevent body scrolling when off-canvas is open
  useEffect(() => {
    if (isOpen) {
      ++openOffCanvasCount;
      // Lock scrolling
      document.body.style.overflow = 'hidden';
      
      // Restore original overflow on cleanup
      return () => {
        --openOffCanvasCount;
        if (openOffCanvasCount === 0) {
          document.body.style.overflow = '';
        }
      };
    }
  }, [isOpen]);

  // Determine the panel width based on device size
  const panelWidth = isMobile ? mobileWidth : width;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: `rgba(0, 0, 0, ${backdropOpacity})`,
          zIndex: zIndex - 10, // Keep backdrop below the panel
          display: isOpen ? 'block' : 'none'
        }}
        onClick={handleBackdropClick}
      />

      {/* OffCanvas Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          [position]: 0,
          width: panelWidth,
          height: '100%',
          backgroundColor: 'white',
          boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
          transition: 'transform 0.3s ease-in-out',
          transform: isOpen ? 'translateX(0)' : `translateX(${position === 'right' ? '100%' : '-100%'})`,
          zIndex: zIndex,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          borderRadius: isMobile ? '0' : '10px'
        }}
      >
        {/* Header */}
        <div style={{ 
          padding: isMobile ? '0.75rem' : '1rem', 
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '80%'
          }}>
            <h4 style={{ 
              margin: 0, 
              fontSize: isMobile ? '1.1rem' : '1.25rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap' 
            }}>{title}</h4>
            {additionalHeaderContent}
          </div>
          <FontAwesomeIcon 
            icon={faTimes} 
            style={{ 
              fontSize: isMobile ? '1.5rem' : '1.2rem', 
              color: '#6c757d', 
              cursor: 'pointer',
              padding: isMobile ? '0.5rem' : '0.2rem' // Larger touch target for mobile
            }}
            onClick={onClose}
          />
        </div>

        {/* Content Area */}
        <div style={{ 
          flex: 1,
          overflowY: 'auto',
          padding: isMobile ? '0.75rem' : '1rem',
          WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
        }}>
          {children}
        </div>

        {/* Footer */}
        {showFooter && (
          <div style={{ 
            padding: isMobile ? '0.75rem' : '1rem',
            borderTop: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem'
          }}>
            {footerContent || (
              <Button
                text="Close"
                variant="outline-secondary"
                onClick={onClose}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default OffCanvas; 